import { MessageData, ForwardedFromUserInfo } from '@chat/interfaces/chat-message-data.interfaces';
import { MessageVm } from '@chat/interfaces/chat.interfaces';

type ForwardableMessage = Pick<
  MessageVm,
  '_id' | 'data' | 'pendingUpload' | 'pendingFailed' | 'is_delivered' | 'isServiceMessage' | 'isCallMessage'
>;

type DisplayUser = Pick<ForwardedFromUserInfo, 'name' | 'surname' | 'nickname'>;

export function isForwardedMessageData(data: MessageData | null | undefined): boolean {
  return !!data?.forwarded_from;
}

export function isForwardedChatMessage(message: Pick<MessageVm, 'data'> | null | undefined): boolean {
  return isForwardedMessageData(message?.data);
}

export function canForwardChatMessage(message: ForwardableMessage | null | undefined): boolean {
  if (!message) return false;
  if (message.isServiceMessage || message.isCallMessage) return false;
  if (typeof message._id !== 'string' || message._id.trim().length === 0) return false;
  if (message.pendingUpload || message.pendingFailed) return false;
  return message.is_delivered !== false;
}

export function composeChatDisplayUserName(user: DisplayUser | null | undefined, fallback: string = ''): string {
  if (!user) return fallback;

  const parts: string[] = [];
  if (typeof user.name === 'string' && user.name.trim().length > 0) {
    parts.push(user.name.trim());
  }
  if (typeof user.surname === 'string' && user.surname.trim().length > 0) {
    parts.push(user.surname.trim());
  }

  const fullName = parts.join(' ').trim();
  if (fullName.length > 0) {
    return fullName;
  }

  if (typeof user.nickname === 'string' && user.nickname.trim().length > 0) {
    return user.nickname.trim();
  }

  return fallback;
}

export function getForwardedFromDisplayName(
  message: Pick<MessageVm, 'data'> | null | undefined,
  fallback: string = '',
): string | null {
  const userInfo = message?.data?.forwarded_from?.user_info;
  if (!userInfo) {
    return null;
  }

  const name = composeChatDisplayUserName(userInfo, fallback);
  return name.length > 0 ? name : null;
}

export function buildForwardSelectionRequestSignature(
  sourceChatId: number,
  targetChatId: number,
  messageIds: string[],
): string {
  return JSON.stringify({
    source_chat_id: sourceChatId,
    target_chat_id: targetChatId,
    message_ids: messageIds,
  });
}
